/* eslint-disable sonarjs/no-duplicate-string */
import natureMarketsSrc from '@assets/images/nature-markets.png';
import { Stack } from '@landler/component-library';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { R1FactType } from '@/api/rest/resources/types/fact';
import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { RecommendationProductCode } from '@/api/rest/resources/types/recommendations';
import {
  BenchmarkExplainerType,
  DefaultWidgetNames,
  GraphTileType,
  NCExplainerType,
  WidgetType,
} from '@/api/rest/resources/types/widget';
import { BetaPill } from '@/components/Pill/BetaPill';
import { getOrganizationSummary } from '@/components/widgets/config/organizationSummary';
import { useGetActivePlotGroup } from '@/components/widgets/config/PlotGroupFilters';
import { Report } from '@/components/widgets/o/ReportCard/ReportCard';
import {
  COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON,
  COMPLIANCE_LOGOS_BIODIVERSITY,
  COMPLIANCE_LOGOS_CANOPY_COVER,
  COMPLIANCE_LOGOS_CANOPY_HEIGHT,
  COMPLIANCE_LOGOS_CARBON,
  COMPLIANCE_LOGOS_WATER,
  CONTACT_SUPPORT_TEAM_FORM_URL,
} from '@/config/constants';
import { useUIStyle } from '@/hooks/useUIStyle';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';
import { formatFactCarbonToCO2, formatFactKilogramsToTonnes } from '@/utils/formatting';

import { useIsLimitedAccessUser } from './useIsLimitedAccessUser';

const widgetRowsStyles = 'flex-col lg:flex-row lg:flex-wrap items-stretch';

export const useDefaultProjectCanopyCoverIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.forest.labels.canopyCover'),
    explainerType: NCExplainerType.canopy_cover,
    showToggle: false,
    showZoom: false,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_CANOPY_COVER,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.forest.labels.averageCanopyCover'),
    popoverProps: {
      title: t('shared.ncaDetail.details.forest.explainers.averageCanopyCover.title'),
      body: t('shared.ncaDetail.details.forest.explainers.averageCanopyCover.body'),
    },
  };

  // TODO: MVP-4772 - change with forest fact
  // this includes all the other places where the BG carbon facts are used in this file
  // TODO: MVP-4772 check if AGC models provide carbon equivalent facts
  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: R1FactType.r1_carbon_bg_previous_1y_uplift_total_pct_change,
    },
    oneYearTrendStatFact: {
      total: R1FactType.unknown,
    },
    benchmarkGraphStatFact: {
      total: R1FactType.unknown,
    },
    benchmarkExplainerType: BenchmarkExplainerType.canopy_cover,
  };

  return {
    name: DefaultWidgetNames.default_project_canopy_cover_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      graphTileType: GraphTileType.canopy_cover,
      footerProps: indicatorFooterProps,
    },
  };
};

export const useDefaultProjectCanopyHeightIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.forest.labels.canopyHeight'),
    explainerType: NCExplainerType.canopy_height,
    showToggle: false,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_CANOPY_HEIGHT,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.forest.labels.averageCanopyHeight'),
    popoverProps: {
      title: t('shared.ncaDetail.details.forest.explainers.averageCanopyHeight.title'),
      body: t('shared.ncaDetail.details.forest.explainers.averageCanopyHeight.body'),
    },
  };

  // TODO: MVP-4772 - change with forest fact
  // this includes all the other places where the BG carbon facts are used in this file
  // TODO: MVP-4772 check if AGC models provide carbon equivalent facts
  const statTileContentProps = {
    latestAnalysisStatFact: {
      // TODO: MVP-4772 - unit will show as t (was temporarily hard coded to display meters in the UI before widgets)
      total: R1FactType.r1_carbon_bg_latest_analysis_total,
    },
    oneYearTrendStatFact: {
      total: R1FactType.unknown,
    },
    benchmarkGraphStatFact: {
      total: R1FactType.unknown,
    },
    benchmarkExplainerType: BenchmarkExplainerType.canopy_height,
  };

  return {
    name: DefaultWidgetNames.default_project_canopy_height_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      graphTileType: GraphTileType.canopy_height,
      footerProps: indicatorFooterProps,
    },
  };
};

const getDefaultProjectForestConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_project_forest,
    type: WidgetType.t_project_indicator_page,
    widgets: [
      {
        name: DefaultWidgetNames.default_project_canopy_height_indicator_card,
        type: WidgetType.o_project_indicator_card,
        visible: true,
      },
      {
        name: DefaultWidgetNames.default_project_canopy_cover_indicator_card,
        type: WidgetType.o_project_indicator_card,
        visible: true,
      },
    ],
  };
};

export const useDefaultProjectCarbonAGCIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbon'),
    explainerType: NCExplainerType.historic_above_ground_carbon,
    showToggle: true,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_ABOVE_GROUND_CARBON,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbonStorage'),
    popoverProps: {
      title: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.aboveGroundCarbonStorage.title'),
      body: t('shared.ncaDetail.details.aboveGroundCarbon.explainers.aboveGroundCarbonStorage.body'),
    },
  };

  // TODO: MVP-4772 - change with forest fact
  // this includes all the other places where the BG carbon facts are used in this file
  // TODO: MVP-4772 check if AGC models provide carbon equivalent facts
  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: R1FactType.r1_carbon_bg_latest_analysis_total,
      average: R1FactType.r1_carbon_bg_latest_analysis_per_ha,
    },
    benchmarkGraphStatFact: {
      total: R1FactType.permanent_soil_carbon_total_benchmark,
      average: R1FactType.permanent_soil_carbon_per_ha_benchmark,
    },
    oneYearChangeStatFact: {
      total: R1FactType.r1_carbon_bg_previous_1y_uplift_total,
      average: R1FactType.r1_carbon_bg_previous_1y_uplift_per_ha,
    },
    oneYearTrendStatFact: {
      total: R1FactType.r1_carbon_bg_previous_1y_uplift_total_pct_change,
      average: R1FactType.r1_carbon_bg_previous_1y_uplift_per_ha_pct_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.historic_below_ground_carbon,
  };

  return {
    name: DefaultWidgetNames.default_project_agc_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      graphTileType: GraphTileType.historic_above_ground_carbon,
      footerProps: indicatorFooterProps,
    },
  };
};

const getDefaultProjectCarbonForestConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_project_forest_carbon,
    type: WidgetType.t_project_indicator_page,
    widgets: [
      {
        name: DefaultWidgetNames.default_project_agc_indicator_card,
        type: WidgetType.o_project_indicator_card,
        visible: true,
      },
    ],
  };
};

export const useDefaultProjectCarbonBGCIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.carbonStorageBG'),
    explainerType: NCExplainerType.historic_below_ground_carbon,
    showToggle: true,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_CARBON,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage'),
    popoverProps: {
      title: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.title'),
      body: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
    },
  };

  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: R1FactType.r1_carbon_bg_latest_analysis_total,
      average: R1FactType.r1_carbon_bg_latest_analysis_per_ha,
    },
    benchmarkGraphStatFact: {
      total: R1FactType.permanent_soil_carbon_total_benchmark,
      average: R1FactType.permanent_soil_carbon_per_ha_benchmark,
    },
    oneYearChangeStatFact: {
      total: R1FactType.r1_carbon_bg_previous_1y_uplift_total,
      average: R1FactType.r1_carbon_bg_previous_1y_uplift_per_ha,
    },
    oneYearTrendStatFact: {
      total: R1FactType.r1_carbon_bg_previous_1y_uplift_total_pct_change,
      average: R1FactType.r1_carbon_bg_previous_1y_uplift_per_ha_pct_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.historic_below_ground_carbon,
    showProjectionsLink: true,
  };

  const definitionProps = {
    body: t('global.measurementExplainer.copy.carbonStorageBody'),
  };

  return {
    name: DefaultWidgetNames.default_project_bgc_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      definitionProps,
      graphTileType: GraphTileType.historic_below_ground_carbon,
      footerProps: indicatorFooterProps,
    },
  };
};

const getDefaultProjectCarbonConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_project_carbon,
    type: WidgetType.t_project_indicator_page,
    widgets: [
      {
        name: DefaultWidgetNames.default_project_bgc_indicator_card,
        type: WidgetType.o_project_indicator_card,
        visible: true,
      },
    ],
  };
};

export const useDefaultProjectWHCIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.water.labels.waterHoldingCapacity'),
    explainerType: NCExplainerType.historic_water_soil_moisture,
    showToggle: true,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_WATER,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.water.labels.waterHoldingCapacityActualAndHistorical'),
    popoverProps: {
      title: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.title'),
      body: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.body'),
    },
  };

  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: R1FactType.r1_water_holding_capacity_latest_analysis_total,
      average: R1FactType.r1_water_holding_capacity_latest_analysis_per_ha,
    },
    benchmarkGraphStatFact: {
      total: R1FactType.water_fc_total_benchmark,
      average: R1FactType.water_fc_per_ha_benchmark,
    },
    oneYearChangeStatFact: {
      total: R1FactType.r1_whc_previous_1y_uplift_total,
      average: R1FactType.r1_whc_previous_1y_uplift_per_ha,
    },
    oneYearTrendStatFact: {
      total: R1FactType.r1_water_whc_increase_previous_1y_uplift_total_pct_change,
      average: R1FactType.r1_water_whc_increase_previous_1y_uplift_per_ha_pct_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.historic_water_soil_moisture,
    showProjectionsLink: true,
  };

  const definitionProps = {
    body: t('global.measurementExplainer.copy.waterHoldingBody'),
  };

  return {
    name: DefaultWidgetNames.default_project_whc_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      definitionProps,
      graphTileType: GraphTileType.historic_water_soil_moisture,
      footerProps: indicatorFooterProps,
    },
  };
};

const getDefaultProjectWaterConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_project_water,
    type: WidgetType.t_project_indicator_page,
    widgets: [
      {
        name: DefaultWidgetNames.default_project_whc_indicator_card,
        type: WidgetType.o_project_indicator_card,
        visible: true,
      },
    ],
  };
};

export const useDefaultSocialIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.social.labels.lifeExpectancy'),
    showToggle: false,
  };

  return {
    name: DefaultWidgetNames.default_template_asset_social,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      graphTileType: GraphTileType.social_life_expectancy,
    },
  };
};

export const useDefaultProjectPOFHIndicator = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: (
      <span className='flex flex-row items-center gap-3'>
        {t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitat')}
        <BetaPill />
      </span>
    ),
    explainerType: NCExplainerType.protected_on_farm_habitat,
    showToggle: false,
  };

  const indicatorFooterProps = {
    logos: COMPLIANCE_LOGOS_BIODIVERSITY,
  };

  const statTileHeaderProps = {
    title: t('shared.ncaDetail.details.biodiversity.labels.protectedOnFarmHabitatActualAndHistorical'),
    popoverProps: {
      title: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.title'),
      body: t('shared.ncaDetail.details.biodiversity.explainers.protectedOnFarmHabitat.body'),
    },
  };

  const definitionProps = {
    body: t('global.measurementExplainer.copy.protectedOnFarmHabitatBody'),
  };

  const statTileContentProps = {
    latestAnalysisStatFact: {
      total: R1FactType.r1_biodiversity_latest_analysis,
    },
    benchmarkGraphStatFact: {
      total: R1FactType.biodiversity_buffer_zone_pct_benchmark_graph,
    },
    oneYearChangeStatFact: {
      total: R1FactType.r1_biodiversity_buffer_zone_pct_previous_1y_uplift_change,
    },
    oneYearTrendStatFact: {
      total: R1FactType.r1_biodiversity_buffer_zone_pct_previous_1y_uplift_change,
      average: R1FactType.r1_biodiversity_buffer_zone_pct_previous_1y_uplift_change,
    },
    benchmarkExplainerType: BenchmarkExplainerType.protected_on_farm_habitat,
  };

  return {
    name: DefaultWidgetNames.default_project_pofh_indicator_card,
    type: WidgetType.o_project_indicator_card,
    widget_format: {
      headerProps: indicatorHeaderProps,
      statTileProps: {
        headerProps: statTileHeaderProps,
        contentProps: statTileContentProps,
      },
      definitionProps,
      graphTileType: GraphTileType.protected_on_farm_habitat,
      footerProps: indicatorFooterProps,
    },
  };
};

export const useDefaultDeforestationIndicator = () => {
  const { t } = useTranslation();

  const deforestationHeaderProps = {
    title: t('global.analysis.deforestation'),
    showToggle: false,
  };

  return {
    name: DefaultWidgetNames.default_project_deforestation_indicator_card,
    type: WidgetType.o_details_graph_card,
    widget_format: {
      headerProps: deforestationHeaderProps,
      graphTileType: GraphTileType.deforestation,
    },
  };
};

const getDefaultProjectPOFHConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_project_pofh,
    type: WidgetType.t_project_indicator_page,
    widgets: [
      {
        name: DefaultWidgetNames.default_project_pofh_indicator_card,
        type: WidgetType.o_project_indicator_card,
        visible: true,
      },
    ],
  };
};

const useDefaultPortfolioConfiguration = () => {
  const hasConservationUi = useUIStyle(OrganizationUIStyle.CONSERVATION);

  return {
    name: DefaultWidgetNames.default_template_portfolio,
    type: WidgetType.t_page,
    widgets: hasConservationUi ? [] : [getOrganizationSummary()],
  };
};

const getDefaultDetailsConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_details,
    type: WidgetType.t_page,
    widgets: [getOrganizationSummary()],
  };
};

const getDefaultDashboardConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_dashboard,
    type: WidgetType.t_page,
    widgets: [
      getOrganizationSummary(),
      {
        name: DefaultWidgetNames.default_two_cards_section,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {},
        widgets: [
          {
            name: DefaultWidgetNames.default_ecosystem_health_chart,
            type: WidgetType.o_c_nature_health_overview,
            visible: true,
          },
        ],
      },
      {
        name: DefaultWidgetNames.default_two_cards_section,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'flex flex-col lg:grid lg:grid-cols-2 justify-between gap-4',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_resilience_drivers_card,
            type: WidgetType.o_c_resilience_drivers_card,
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_climate_risk_aggregated_overview,
            type: WidgetType.o_c_risk_aggregated_overview,
            visible: true,
          },
        ],
      },
      {
        name: DefaultWidgetNames.default_two_cards_section,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'lg:grid lg:grid-cols-2 justify-between gap-4',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_ranked_by_resilience_plot_list,
            type: WidgetType.o_c_ranked_by_resilience_plot_list,
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_ranked_by_risk_plot_list,
            type: WidgetType.o_c_ranked_by_risk_plot_list,
            visible: true,
          },
        ],
      },
    ],
  };
};

const useDefaultCarbonConfiguration = () => {
  const { t } = useTranslation();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();
  const showSummaryCard = !isLimitedAccessUser && !isNullish(activeFilters) && activeFilters.length === 0;

  const abcHeaderProps = {
    title: t('shared.ncaDetail.details.aboveGroundCarbon.labels.aboveGroundCarbonStorage'),
    headerTooltip: t('shared.ncaDetail.details.carbon.explainers.aboveTheGroundCarbonStorage.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  const bgcHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage'),
    headerTooltip: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  return {
    name: DefaultWidgetNames.default_template_asset_carbon,
    type: WidgetType.t_asset_page,
    widgets: [
      {
        name: 'carbon_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: widgetRowsStyles,
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_carbon_total,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.carbon_combined_per_ha,
              headerText: 'global.widget.combinedCarbon',
              tooltipText: 'global.widget.resilienceDrivers.combinedCarbon.tooltip.description',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_current_bgc_co2,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.carbon_combined_per_ha,
              headerText: 'global.widget.currentCombinedCo2Equivalent',
              tooltipText: 'global.widget.currentCombinedCo2EquivalentTooltip',
              formatter: formatFactCarbonToCO2,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_carbon_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.carbon_combined_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgCarbonUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_carbon_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.carbon_combined_previous_1y_pct_change,
              headerText: 'global.widget.yearlyUpliftOneYear',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'carbon_summary',
        type: WidgetType.o_recommendation_summary,
        visible: showSummaryCard,
        relatedData: {
          productCode: RecommendationProductCode.EK_CARBON,
        },
      },
      {
        name: 'asset_agc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: abcHeaderProps,
          graphTileType: GraphTileType.historic_above_ground_carbon,
        },
      },
      {
        name: 'asset_bgc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: bgcHeaderProps,
          graphTileType: GraphTileType.historic_below_ground_carbon,
        },
      },
    ],
  };
};

const useDefaultAssetBelowGroundCarbonConfiguration = () => {
  const { t } = useTranslation();

  const isLimitedAccessUser = useIsLimitedAccessUser();
  const activeFilters = useGetActivePlotGroup();
  const showSummaryCard = !isLimitedAccessUser && !isNullish(activeFilters) && activeFilters.length === 0;

  const bgcHeaderProps = {
    title: t('shared.ncaDetail.details.carbon.labels.belowTheGroundCarbonStorage'),
    headerTooltip: t('shared.ncaDetail.details.carbon.explainers.belowTheGroundCarbonStorage.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  return {
    name: DefaultWidgetNames.default_template_asset_bgc,
    type: WidgetType.t_asset_page,
    widgets: [
      {
        name: 'bgc_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: widgetRowsStyles,
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_bgc,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_carbon_bg_latest_analysis_per_ha,
              headerText: 'global.widget.currentBgc',
              tooltipText: 'global.widget.currentBgcTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_current_bgc_co2,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_carbon_bg_latest_analysis_per_ha,
              headerText: 'global.widget.currentBgcCo2Equivalent',
              tooltipText: 'global.widget.currentBgcCo2EquivalentTooltip',
              formatter: formatFactCarbonToCO2,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_bgc_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.permanent_soil_carbon_per_ha_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgBgcUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
              formatter: formatFactKilogramsToTonnes,
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_bgc_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_carbon_bg_previous_1y_uplift_per_ha_pct_change,
              headerText: 'global.widget.projection1yBgcUplift',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'carbon_summary',
        type: WidgetType.o_recommendation_summary,
        visible: showSummaryCard,
        relatedData: {
          productCode: RecommendationProductCode.EK_CARBON,
        },
      },
      {
        name: 'asset_bgc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: bgcHeaderProps,
          graphTileType: GraphTileType.historic_below_ground_carbon,
        },
      },
    ],
  };
};

const useDefaultAssetWaterConfiguration = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.water.labels.waterHoldingCapacity'),
    headerTooltip: t('shared.ncaDetail.details.water.explainers.waterHoldingCapacity.body'),
    showToggle: true,
    showZoom: true,
    showGraphType: true,
  };

  return {
    name: DefaultWidgetNames.default_template_asset_water,
    type: WidgetType.t_asset_page,
    widgets: [
      {
        name: 'water_asset_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: widgetRowsStyles,
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_whc,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_water_holding_capacity_latest_analysis_per_ha,
              headerText: 'global.widget.currentWhc',
              tooltipText: 'global.widget.currentWhcTootip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_water_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.water_fc_per_ha_previous_5y_pct_change,
              headerText: 'global.widget.historic5yAvgWaterUplift',
              tooltipText: 'global.widget.historic5yUpliftTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_water_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.r1_water_whc_increase_previous_1y_uplift_per_ha_pct_change,
              headerText: 'global.widget.projection1yWaterUplift',
              tooltipText: 'global.widget.projection1yUpliftTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'asset_whc_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: indicatorHeaderProps,
          graphTileType: GraphTileType.historic_water_soil_moisture,
        },
      },
    ],
  };
};

const useDefaultAssetSocialConfiguration = () => {
  const { t } = useTranslation();

  const indicatorHeaderProps = {
    title: t('shared.ncaDetail.details.social.labels.lifeExpectancy'),
    showToggle: false,
    showGraphType: true,
  };

  return {
    name: DefaultWidgetNames.default_template_asset_social,
    type: WidgetType.t_asset_page,
    widgets: [
      {
        name: 'social_measurement_summary_row',
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          direction: 'row',
          center: true,
          spacing: 6,
          className: widgetRowsStyles,
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_fact_card_current_social,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.social_life_expectancy,
              headerText: 'global.widget.currentSocial',
              // TODO: MVP-7534 - add tooltip text when copy is available
              // tooltipText: 'global.widget.currentSocialTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_historic_social_5y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.social_life_expectancy_previous_5y_avg_uplift,
              headerText: 'global.widget.historic5yAvgSocialUplift',
              // TODO: MVP-7534 - add tooltip text when copy is available
              // tooltipText: 'global.widget.historic5yUpliftSocialTooltip',
            },
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_fact_card_projection_social_1y_uplift,
            type: WidgetType.o_fact_summary,
            relatedData: {
              factType: R1FactType.social_life_expectancy_previous_1y_uplift,
              headerText: 'global.widget.projection1ySocialUplift',
              // TODO: MVP-7534 - add tooltip text when copy is available
              // tooltipText: 'global.widget.projection1yUpliftSocialTooltip',
            },
            visible: true,
          },
        ],
      },
      {
        name: 'measurements_social_indicator_card',
        type: WidgetType.o_details_graph_card,
        visible: true,
        relatedData: {
          headerProps: indicatorHeaderProps,
          graphTileType: GraphTileType.social_life_expectancy,
        },
      },
    ],
  };
};

const useDefaultNaturalCapitalConfiguration = () => {
  return {
    name: DefaultWidgetNames.default_template_natural_capital,
    type: WidgetType.t_page,
    widgets: [
      {
        name: 'capital_summary_row',
        type: WidgetType.a_stack,
        visible: false,
        relatedData: {
          direction: 'row',
          spacing: 6,
          className: 'flex-wrap lg:flex-nowrap items-stretch',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_total_account_value_card,
            type: WidgetType.o_total_account_value,
            visible: false,
          },
        ],
      },
      {
        name: DefaultWidgetNames.default_two_cards_section,
        type: WidgetType.a_stack,
        visible: true,
        relatedData: {
          className: 'flex flex-col lg:grid lg:grid-cols-2 justify-between gap-4',
        },
        widgets: [
          {
            name: DefaultWidgetNames.default_land_distribution_chart,
            type: WidgetType.o_c_land_distribution_chart,
            visible: true,
          },
          {
            name: DefaultWidgetNames.default_earnings_chart,
            type: WidgetType.o_c_earnings_chart,
            visible: false,
          },
          {
            name: DefaultWidgetNames.default_nca_metrics_sidebar,
            type: WidgetType.a_stack,
            visible: true,
            relatedData: {
              direction: 'column',
              spacing: 4,
              className: 'flex flex-col h-full max-w-md ml-auto',
            },
            widgets: [
              {
                name: DefaultWidgetNames.default_total_account_value_card,
                type: WidgetType.o_total_account_value,
                visible: true,
              },
              {
                name: DefaultWidgetNames.default_stewarded_value_card,
                type: WidgetType.o_stewarded_value,
                visible: true,
              },
              {
                name: DefaultWidgetNames.default_ecological_integrity_card,
                type: WidgetType.o_ecological_integrity_index,
                visible: true,
              },
            ],
          },
        ],
      },
    ],
  };
};

enum KnowledgeReportNames {
  natureMarkets = 'natureMarkets',
}

export const useDefaultKnowledgeConfiguration = () => {
  const { t } = useTranslation();
  const [activeReport, setActiveReport] = useState<Report | null>(null);

  const reports = [
    {
      id: KnowledgeReportNames.natureMarkets as string,
      report_name: t('knowledgeBase.reports.natureMarkets.header'),
      report_date: new Date().toISOString(),
      created_at: new Date().toISOString(),
      file_download_url: '',
      thumbnail_url: natureMarketsSrc,
      to: CONTACT_SUPPORT_TEAM_FORM_URL,
    },
  ] as Report[];

  const onClickReport = (report: Report) => {
    setActiveReport(report);
  };

  return {
    name: DefaultWidgetNames.default_template_knowledge,
    type: WidgetType.t_page,
    widgets: [
      {
        name: 'default_knowledge_cards',
        type: WidgetType.o_c_knowledge_cards,
        visible: isNullish(activeReport),
        relatedData: {
          articles: reports,
          onArticleClick: onClickReport,
        },
      },
      {
        name: 'default_knowledge_report',
        type: WidgetType.o_card,
        visible: !isNullish(activeReport),
        relatedData: {
          header: !isNullish(activeReport) && (
            <Trans i18nKey={`knowledgeBase.reports.${activeReport.id as keyof typeof KnowledgeReportNames}.header`} />
          ),
          body: !isNullish(activeReport) && (
            <Stack className='gap-8'>
              {!isNullish(activeReport.thumbnail_url) && (
                <img
                  src={activeReport.thumbnail_url}
                  alt={`Thumbnail for ${activeReport.report_name}`}
                  className='max-h-full max-w-full object-contain rounded-lg mb-4'
                />
              )}
              <Trans
                i18nKey={`knowledgeBase.reports.${activeReport.id as keyof typeof KnowledgeReportNames}.body`}
                components={{
                  h3: <span className='typography-h3 text-text-primary whitespace-pre-wrap' />,
                }}
              />
            </Stack>
          ),
          footer: !isNullish(activeReport) && (
            <Trans
              i18nKey={`knowledgeBase.reports.${activeReport.id as keyof typeof KnowledgeReportNames}.footer`}
              components={{
                /* eslint-disable-next-line jsx-a11y/anchor-has-content */
                Link: <a className='underline' href={activeReport.to} target='_blank' rel='noreferrer' />,
              }}
            />
          ),
          styles: {
            root: 'border-none w-full px-4 whitespace-pre-wrap',
            header: 'border-none typography-h2 text-text-primary px-0 font-semibold',
            body: 'border border-none typography-body1 text-text-secondary',
            footer:
              'border border-none typography-body1Semibold text-text-primary bg-neutral-black-4 rounded-2xl m-4 mt-8',
          },
        },
      },
    ],
  };
};

export const DEFAULT_INDICATOR_WIDGET_PROPS_BY_NAME = {
  [DefaultWidgetNames.default_project_canopy_cover_indicator_card]: useDefaultProjectCanopyCoverIndicator,
  [DefaultWidgetNames.default_project_canopy_height_indicator_card]: useDefaultProjectCanopyHeightIndicator,
  [DefaultWidgetNames.default_project_agc_indicator_card]: useDefaultProjectCarbonAGCIndicator,
  [DefaultWidgetNames.default_project_bgc_indicator_card]: useDefaultProjectCarbonBGCIndicator,
  [DefaultWidgetNames.default_project_whc_indicator_card]: useDefaultProjectWHCIndicator,
  [DefaultWidgetNames.default_project_pofh_indicator_card]: useDefaultProjectPOFHIndicator,
  [DefaultWidgetNames.default_project_deforestation_indicator_card]: useDefaultDeforestationIndicator,
  [DefaultWidgetNames.default_project_social]: useDefaultSocialIndicator,
};

export const DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME = {
  [DefaultWidgetNames.default_template_project_forest]: () => null,
  [DefaultWidgetNames.default_template_project_forest_carbon]: () => null,
  [DefaultWidgetNames.default_template_project_carbon]: () => null,
  [DefaultWidgetNames.default_template_project_water]: () => null,
  [DefaultWidgetNames.default_template_project_pofh]: () => null,
  [DefaultWidgetNames.default_template_asset_carbon]: () => null,
  [DefaultWidgetNames.default_template_asset_bgc]: () => null,
  [DefaultWidgetNames.default_template_asset_water]: () => null,
  [DefaultWidgetNames.default_template_asset_pofh]: () => null,
  [DefaultWidgetNames.default_template_asset_social]: () => null,
  [DefaultWidgetNames.default_template_natural_capital]: () => null,
  [DefaultWidgetNames.default_recommendation_card]: () => null,
  [DefaultWidgetNames.default_template_portfolio]: () => null,
  [DefaultWidgetNames.default_template_dashboard]: () => null,
  ...DEFAULT_INDICATOR_WIDGET_PROPS_BY_NAME,
};

export const DEFAULT_WIDGET_CONFIGURATION_BY_WIDGET_NAME = {
  [DefaultWidgetNames.default_template_project_forest]: getDefaultProjectForestConfiguration,
  [DefaultWidgetNames.default_template_project_forest_carbon]: getDefaultProjectCarbonForestConfiguration,
  [DefaultWidgetNames.default_template_project_carbon]: getDefaultProjectCarbonConfiguration,
  [DefaultWidgetNames.default_template_project_water]: getDefaultProjectWaterConfiguration,
  [DefaultWidgetNames.default_template_project_pofh]: getDefaultProjectPOFHConfiguration,
  [DefaultWidgetNames.default_template_asset_carbon]: useDefaultCarbonConfiguration,
  [DefaultWidgetNames.default_template_asset_bgc]: useDefaultAssetBelowGroundCarbonConfiguration,
  [DefaultWidgetNames.default_template_asset_water]: useDefaultAssetWaterConfiguration,
  [DefaultWidgetNames.default_template_asset_pofh]: () => null,
  [DefaultWidgetNames.default_template_asset_social]: useDefaultAssetSocialConfiguration,
  [DefaultWidgetNames.default_template_natural_capital]: useDefaultNaturalCapitalConfiguration,
  [DefaultWidgetNames.default_template_knowledge]: useDefaultKnowledgeConfiguration,
  [DefaultWidgetNames.default_project_agc_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_bgc_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_whc_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_pofh_indicator_card]: () => null,
  [DefaultWidgetNames.default_project_deforestation_indicator_card]: () => null,
  [DefaultWidgetNames.default_template_portfolio]: useDefaultPortfolioConfiguration,
  [DefaultWidgetNames.default_template_dashboard]: getDefaultDashboardConfiguration,
  [DefaultWidgetNames.default_template_details]: getDefaultDetailsConfiguration,
};

export const createDefaultWidgetFromSlot = (
  name: string,
  type: WidgetType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  relatedData?: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): { name: string; type: WidgetType; widget_format?: any } => {
  const generator = DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME[name as keyof typeof DEFAULT_WIDGET_PROPS_BY_WIDGET_NAME];

  if (typeof generator === 'function') {
    try {
      const widget = generator();
      if (widget) {
        return widget;
      }
    } catch (error) {
      Logger.error(`Failed to create default widget: ${name} of type: ${type}`);
      Logger.error(error as Error);
    }
  }

  return {
    name,
    type,
    ...(relatedData ? { widget_format: relatedData } : {}),
  };
};
